import { useSyncExternalStore } from 'react';

/**
 * The seam between the public storefront page and its account layer.
 *
 * A visitor's page (SitePage) is light: no auth provider, no Matrix, no app
 * store. Signing in, the user menu and in-place editing live in SiteAccountLayer,
 * a lazy chunk that loads only for a visitor who is (or asks to be) signed in.
 * The two share this tiny store: the page publishes where the menu goes and how
 * big it should be; the layer reports that the menu is up and when the signed-in
 * user is editing (the page then steps aside for the editor).
 */
export interface SiteAccountState {
  /** The element in the top bar the user menu is drawn into. */
  slotEl: HTMLElement | null;
  compact: boolean;
  /** The layer's user menu is mounted (the page's placeholder button hides). */
  menuReady: boolean;
  /** The signed-in user may edit this site: the editor replaces the page. */
  editing: boolean;
  /** The account layer should load (a stored session, or the visitor asked). */
  requested: boolean;
  /** The visitor asked to sign in: open the dialog once the layer is up. */
  openSignup: boolean;
}

/** The top bar's round menu button, drawn by the placeholder and by the real user menu alike. */
export const SITE_MENU_BUTTON_CLASS = 'p-2 rounded-full bg-black/60 hover:bg-black/80 text-gray-300 border border-white/20';

let state: SiteAccountState = { slotEl: null, compact: false, menuReady: false, editing: false, requested: false, openSignup: false };
const listeners = new Set<() => void>();

export function patchSiteAccount(patch: Partial<SiteAccountState>): void {
  const next = { ...state, ...patch };
  if ((Object.keys(next) as Array<keyof SiteAccountState>).every((key) => next[key] === state[key])) return;
  state = next;
  listeners.forEach((listener) => listener());
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export function useSiteAccount(): SiteAccountState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}
